import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Input, useInput, Card, Spacer, Text, Grid } from "@nextui-org/react";
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { Calendar } from 'primereact/calendar';
import { formatDateLong, formatDateWeekday } from '../../utils.js';
import { Panel } from 'primereact/panel';
import { OrderService } from '../service/OrderService';
import { Dropdown } from 'primereact/dropdown';

import { AutoComplete as PrimeAutoComplete } from 'primereact/autocomplete';
import { EditableField, NonEditableField } from './Fields';

const formatPhoneNumber = (phoneNumber) => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phoneNumber;
};

const Customer = ({ order, customer, editable, handleCustomerChange, linkInvoiceToOrder, customers, filteredPhoneNumbers, filteredEmails }) => {


  useEffect(() => {
    // console.log("Customers Fetched:", customers);
  }, [customers]);


  const [displayModal, setDisplayModal] = useState(false);
  const [potentialCustomers, setPotentialCustomers] = useState([]);
  const [potentialInvoices, setPotentialInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);


  const [customersProcessed, setCustomersProcessed] = useState([]);

  useEffect(() => {
    setCustomersProcessed(processCustomers(customers));
  }, [customers]);


  const processCustomers = (customers) => {
    // console.log("Processing Customers:", customers);
    return customers
      .filter(customer => customer.family_name && customer.given_name) // Step 1
      .map(customer => ({
        ...customer,
        fullName: customer.given_name + ' ' + customer.family_name, // Step 2
        phone: customer.phone,
        email: customer.email,
      }));
  };
  


  const transformKeys = (obj) => {
    const camelCaseKey = (key) => {
      return key.replace(/([-_][a-z])/ig, ($1) => {
        return $1.toUpperCase().replace('-', '').replace('_', '');
      });
    };

    if (Array.isArray(obj)) {
      return obj.map((v) => transformKeys(v));
    } else if (obj !== null && obj.constructor === Object) {
      return Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [camelCaseKey(key)]: transformKeys(obj[key]),
        }),
        {}
      );
    }
    return obj;
  };

  const handleSearchForCustomer = async () => {
    setDisplayModal(true);
    const [firstName, lastName] = customer.name.split(' ');

    const potentialCustomersByFirstName = await OrderService.getSquareCustomersByFirstName(firstName);
    const potentialCustomersByLastName = await OrderService.getSquareCustomersByLastName(lastName);
    const potentialCustomersSquareEmail = customer.email ? await OrderService.searchSquareCustomerEmail(customer.email) : null;
    const potentialCustomersSquarePhone = customer.phone ? await OrderService.searchSquareCustomerPhone(customer.phone) : null;
    // console.log("Potential Customers Square Email:", potentialCustomersSquareEmail);
    // console.log("Potential Customers Square Phone:", potentialCustomersSquarePhone);

    // Combine results
    const combinedCustomers = [...potentialCustomersByFirstName, ...potentialCustomersByLastName,
    ...potentialCustomersSquareEmail.customers, ...potentialCustomersSquarePhone.customers
    ];

    // Remove duplicates based on .id
    const uniqueCustomers = Object.values(
      combinedCustomers.reduce((acc, cur) => {
        acc[cur.id] = cur;
        return acc;
      }, {})
    );
    const camelCasedUniqueCustomers = transformKeys(uniqueCustomers);
    setPotentialCustomers(camelCasedUniqueCustomers);
  };


  const selectPotentialCustomer = async (selectedCustomer) => {
    const invoices = await OrderService.searchSquareInvoicesBySquareCustomerID(selectedCustomer.id.toString());
    // console.log("Invoices Fetched for Selected Customer:", invoices.invoices);
    setPotentialInvoices(invoices.invoices);
  };


  const linkInvoiceToOrderParent = () => {
    linkInvoiceToOrder(order, selectedInvoice);
    setDisplayModal(false);
    setPotentialCustomers([]);
    setPotentialInvoices([]);
    setSelectedInvoice(null);
  };

  const cancelLinking = () => {
    setDisplayModal(false);
    setPotentialCustomers([]);
    setPotentialInvoices([]);
    setSelectedInvoice(null);
  };

  const renderTableHeader = (header) => (
    <div className="table-header">
      {header}
    </div>
  );

  const customStyles = {
    '.table-header': {
      fontWeight: 'bold',
      fontSize: '1.2em',
      margin: '0.5em 0'
    },
    '.modal-buttons': {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '1em'
    }
  };



  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Format the phone number if the input is for the phone field
    const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;

    const updatedCustomer = { ...customer, [name]: formattedValue };
    handleCustomerChange(updatedCustomer);
  };

  const handleDateChange = (e, field) => {
    const updatedCustomer = {
      ...customer,
      [field]: e.target.value,
    };
    handleCustomerChange(updatedCustomer);
  };



  const updateExistingCustomer = (customer) => {
    // OrderService.updateSquareCustomer(customer)
  };

  const createNewCustomer = (customer) => {
    // OrderService.addSquareCustomer(customer)
  };

  const { value, reset, bindings } = useInput("");

  const validateEmail = (value) => {
    return value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}$/i);
  };

  const helper = React.useMemo(() => {
    if (!value)
      return {
        text: "",
        color: "",
      };
    const isValid = validateEmail(value);
    return {
      text: isValid ? "Correct email" : "Enter a valid email",
      color: isValid ? "success" : "error",
    };
  }, [value]);

  return (
    <>
      <Dialog visible={displayModal} onHide={() => {
        setDisplayModal(false);
        setPotentialCustomers([]);
        setPotentialInvoices([]);
        setSelectedInvoice(null);
      }}
        style={{ minWidth: '50vw', maxWidth: '50vw' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', }}>
          {/* Customers Table */}
          <div style={{ flex: '1', overflowY: 'auto', maxHeight: '400px', marginRight: '10px' }}>
            <DataTable
              value={potentialCustomers}
              header={renderTableHeader("Choose a Customer")}
              selectionMode="single"
              onSelectionChange={(e) => selectPotentialCustomer(e.value)}
            >
              <Column field="givenName" header="First Name" sortable></Column>
              <Column field="familyName" header="Last Name" sortable></Column>
              <Column field="creationSource" header="Source" sortable></Column>
              <Column field="createdAt" header="Created At" sortable></Column>
            </DataTable>
          </div>

          {/* Invoices Table */}
          {potentialInvoices?.length > 0 && (
            <div style={{ flex: '1', overflowY: 'auto', maxHeight: '400px' }}>
              <DataTable value={potentialInvoices}
                onSelectionChange={(e) => setSelectedInvoice(e.value)}
                selectionMode="single"
                header={renderTableHeader("Choose an Invoice")}
              >
                <Column field="invoiceNumber" header="Invoice Number" size="small" sortable></Column>
                <Column field="title" header="Title" sortable></Column>
                <Column header="Customer Name" body={rowData => `${rowData.primaryRecipient.givenName} ${rowData.primaryRecipient.familyName}`}></Column>
                <Column field="primaryRecipient.emailAddress" header="Email" sortable></Column>
                <Column field="status" header="Status" sortable></Column>
                {/* Uncomment if you have the renderLinkButton function */}
                {/* <Column header="Action" body={renderLinkButton}></Column> */}
              </DataTable>
            </div>
          )}
        </div>

        <div className="modal-buttons">
          <Button label="Link Invoice"
            disabled={!selectedInvoice}
            onClick={linkInvoiceToOrderParent} />
          <Button label="Cancel" onClick={cancelLinking} />
        </div>
      </Dialog>

      <div style={{
        paddingTop: '0.25rem',
        paddingBottom: '0.25rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        border: '1px solid #eaeaea',
        borderRadius: '10px',
        background: 'black',
        marginBottom: '0.5rem',
        marginTop: '0.5rem',
        marginLeft: '0.5rem',
        marginRight: '0.5rem',
        textAlign: 'center',
        fontSize: '1.25rem',
        fontWeight: 'bold',
        color: 'white',
        //letter spacing
        letterSpacing: '0.15rem',
        marginTop: '10px'
      }}>



        <strong>Customer Details</strong>
      </div>
      <Grid.Container gap={2}>
        <Grid >
          {editable || (customer.name && customer.name !== '') ?
            editable ?
              <AutoCompleteField
                label="Customer Name"
                value={customer.name}
                suggestions={customersProcessed}
                onChange={handleInputChange}
                onSelect={(selectedCustomer) => handleCustomerChange({ ...customer, name: selectedCustomer.fullName })}
              /> :
              <NonEditableField label="Customer Name" value={customer.name} />
            : null
          }
        </Grid>
        <Grid >
          {editable || (customer.phone && customer.phone !== '') ?
            editable ?
              <AutoCompleteFieldPhone
                label="Phone Number"
                value={customer.phone}
                suggestions={filteredPhoneNumbers}
                onChange={handleInputChange}
                onSelect={(selectedPhone) => handleCustomerChange({ ...customer, ...selectedPhone })}
              /> :
              <NonEditableField label="Phone Number" value={formatPhoneNumber(customer.phone)} />
            : null
          }
        </Grid>
        <Grid >
          {editable || (customer.email && customer.email !== '') ?
            editable ?
              <AutoCompleteFieldEmail
                label="Email Address"
                value={customer.email}
                suggestions={filteredEmails}
                onChange={handleInputChange}
                onSelect={(selectedEmail) => handleCustomerChange({ ...customer, ...selectedEmail })}
              /> :
              <NonEditableField label="Email Address" value={customer.email} />
            : null
          }
        </Grid>
        <Grid >
          {editable || (customer.contactDate && customer.contactDate !== '') ?
            editable ?
              <EditableField
                label="Contact Date"
                name="contactDate"
                value={customer.contactDate}
                onChange={(e) => handleDateChange(e, 'contactDate')}
                type="calendar"
              /> :
              <NonEditableField label="Contact Date" value={new Date(customer.contactDate).toLocaleDateString()} />
            : null
          }
        </Grid>
        <Grid >
          {editable || (customer.internal_notes && customer.internal_notes !== '') ?
            editable ?
              <EditableField
                label="Internal Note"
                name="internal_notes"
                value={customer.internal_notes}
                onChange={handleInputChange}
                type="text"
              /> :
              <NonEditableField label="Internal Note" value={customer.internal_notes} />
            : null
          }
        </Grid>
        {
          !editable && (!order?.squareCustomerID || order?.squareCustomerID === '') ?
            <Grid>
              <Button
                rounded
                
                outlined
                onClick={handleSearchForCustomer}
              >
                Link Customer
              </Button>
            </Grid>
            : null
        }

      </Grid.Container>
    </>

  );
};

export default Customer;


const AutoCompleteField = ({ label, value, suggestions, onChange, onSelect }) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const searchSuggestions = (event) => {
    let results = suggestions.filter((suggestion) => {
      return suggestion.fullName.toLowerCase().startsWith(event.query.toLowerCase());
    });
    setFilteredSuggestions(results);
  };

  const onValueSelect = (e) => {
    onSelect(e.value); // This will call handleCustomerChange in the parent component
  };

  const handleInputChange = (e) => {
    const newValue = e.value || ''; // Fallback to an empty string if e.value is undefined
    onChange({ target: { name: 'name', value: newValue } });
  };

  return (
    <div className="field">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label>{label}</label>
        <PrimeAutoComplete
          value={value}
          suggestions={filteredSuggestions}
          completeMethod={searchSuggestions}
          field="fullName"
          onSelect={onValueSelect}
          onChange={handleInputChange} // Updated to use handleInputChange
        />
      </div>
    </div>
  );
};


const AutoCompleteFieldEmail = ({ label, value, suggestions, onChange, onSelect }) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const searchSuggestions = (event) => {
    let results = suggestions.filter((suggestion) => {
      return suggestion.email ? suggestion.email.toLowerCase().startsWith(event.query.toLowerCase()) : false;
    });
    setFilteredSuggestions(results);
  };

  const onValueSelect = (e) => {
    onSelect({ email: e.value.email }); // Update only the email field
  };

  const handleInputChange = (e) => {
    const newValue = e.value || ''; // Fallback to an empty string if e.value is undefined
    onChange({ target: { name: 'email', value: newValue } });
  };

  return (
    <div className="field">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label>{label}</label>
        <PrimeAutoComplete
          value={value}
          suggestions={filteredSuggestions}
          completeMethod={searchSuggestions}
          field="email"
          onSelect={onValueSelect}
          onChange={handleInputChange} // Updated to use handleInputChange
        />
      </div>
    </div>
  );
};

const AutoCompleteFieldPhone = ({ label, value, suggestions, onChange, onSelect }) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const searchSuggestions = (event) => {
    let results = suggestions.filter((suggestion) => {
      return suggestion.phone ? suggestion.phone.toLowerCase().startsWith(event.query.toLowerCase()) : false;
    });
    setFilteredSuggestions(results);
  };

  const onValueSelect = (e) => {
    onSelect({ phone: e.value.phone }); // Update only the phone field
  };

  const handleInputChange = (e) => {
    const newValue = e.value || ''; // Fallback to an empty string if e.value is undefined
    onChange({ target: { name: 'phone', value: newValue } });
  };

  return (
    <div className="field">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label>{label}</label>
        <PrimeAutoComplete
          value={value}
          suggestions={filteredSuggestions}
          completeMethod={searchSuggestions}
          field="phone"
          onSelect={onValueSelect}
          onChange={handleInputChange} // Updated to use handleInputChange
        />
      </div>
    </div>
  );
};

